import type { PlanRequest, PlanResponse, PlannerConfig } from '../shared/contracts.js';
import { ApiOfflineError } from './observed-request.js';

export async function fetchPlannerConfig(): Promise<PlannerConfig> {
  let response: Response;
  try { response = await fetch('/api/config'); } catch (error) { throw new ApiOfflineError({ cause: error }); }
  const config = await response.json() as PlannerConfig;
  if (!response.ok || !Number.isInteger(config.maxRoutePoints) || config.maxRoutePoints < 2 ||
    !Number.isInteger(config.maxExtraMinutes) || config.maxExtraMinutes < 0 || typeof config.coverage !== 'string') {
    throw new Error('Planner limits could not be loaded. Reload to try again.');
  }
  return config;
}

interface PlanCallbacks {
  /** The newest plan, with the request it answers. */
  onResult: (result: PlanResponse, request: PlanRequest) => void;
  onError: (error: Error) => void;
  onBusy: (busy: boolean) => void;
}

/** One in-flight request and only the newest settled edit, never a request queue. */
export class PlanCoordinator {
  private revision = 0;
  private running = false;
  private disposed = false;
  private timer?: ReturnType<typeof setTimeout>;
  private pending?: { request: PlanRequest; revision: number; ready: boolean };

  constructor(
    private readonly transport: (request: PlanRequest) => Promise<PlanResponse>,
    private readonly callbacks: PlanCallbacks
  ) {}

  invalidate(): void {
    this.revision++;
    clearTimeout(this.timer);
    this.pending = undefined;
  }

  schedule(request: PlanRequest, settleMs = 350): void {
    if (this.disposed) return;
    this.invalidate();
    this.pending = { request, revision: this.revision, ready: settleMs === 0 };
    if (settleMs === 0) this.startNext();
    else this.timer = setTimeout(() => {
      if (this.pending) this.pending.ready = true;
      this.startNext();
    }, settleMs);
  }

  dispose(): void {
    this.disposed = true;
    this.invalidate();
    // Do not abort the transport and start another search on the assumption
    // that a browser disconnect cancels GraphHopper's work.
  }

  private startNext(): void {
    if (this.disposed || this.running || !this.pending?.ready) return;
    const next = this.pending;
    this.pending = undefined;
    this.running = true;
    this.callbacks.onBusy(true);
    void this.run(next);
  }

  private async run(next: { request: PlanRequest; revision: number }): Promise<void> {
    try {
      const response = await this.transport(next.request);
      if (!this.disposed && next.revision === this.revision) this.callbacks.onResult(response, next.request);
    } catch (error) {
      if (!this.disposed && next.revision === this.revision) {
        this.callbacks.onError(error instanceof Error ? error : new Error('The route could not be planned.'));
      }
    } finally {
      this.running = false;
      if (!this.disposed) {
        if (this.pending?.ready) this.startNext();
        else this.callbacks.onBusy(false);
      }
    }
  }
}
